import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Space } from 'antd';

import { routerPath } from 'src/Config';
import { i18, trb, SIGN_CANCEL_TYPES, translationGroups } from 'src/Utilities';
import { BulkFileActions, OMCActions } from 'src/Redux';
import { ContentNoData, Notification, OperationApproval } from 'src/Components';
import { AlertNotification } from 'src/shared';
import { PaymentError } from '../../components';
import { TableUnsignedPaymentFiles } from '../../../../Components/TableUnsignedPaymentFiles/TableUnsignedPaymentFiles.tsx';
import { SummaryUnsignedPaymentFiles } from '../../../../Components/SummaryUnsignedPaymentFiles/SummaryUnsignedPaymentFiles.tsx';
import { ReportUnsignedPaymentFiles } from '../../../../Components/ReportUnsignedPaymentFiles/ReportUnsignedPaymentFiles.tsx';
import { BlockedOutgoingPaymentsNotification } from '../../../../Components/BlockedOutgoingPaymentsNotification/BlockedOutgoingPaymentsNotification.tsx';

const POLLING_INTERVAL = 3000;

export const UnsignedPaymentFileDetails = () => {
  const dispatch = useDispatch();
  const params = useParams();
  const navigate = useNavigate();

  const refExcludedPayments = useRef<HTMLDivElement | null>(null);

  const [loadingPreviewType, setLoadingPreviewType] = useState<string | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [approve, setApprove] = useState(false);
  const [errors, setErrors] = useState([]);
  const [isVopPolling, setIsVopPolling] = useState(false);

  const { sendSUOR, sendSUORSMS } = useSelector((state: any) => state.OMCReducers);
  const { MALR } = useSelector((state: any) => state.UACReducers);
  const isOutgoingPaymentsBlocked = MALR.ManagedAccounts.find((item: any) => item.IsActive)?.OutgoingPaymentsBlocked;

  const { FilesListDetails, loading, error, FilesListPreview, FilesListSign } = useSelector((state: any) => state.BulkFileReducer);
  const { AuthType } = useSelector((state: any) => state.UACReducers?.ATR);
  const { language } = useSelector((state: any) => state.languageReducer);

  const File = FilesListDetails;

  const VopPassed = File?.VopPassed;

  useEffect(() => {
    window.scrollTo(0, 0);
    dispatch(BulkFileActions.getBulkPaymentsFileDetails(params.FileGuid as string) as any);
  }, []);

  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;

    if (!VopPassed && File?.FileGuid && File?.ReturnValue?.length) {
      setIsVopPolling(true);

      interval = setInterval(() => {
        dispatch(BulkFileActions.getBulkPaymentsFileDetails(params.FileGuid as string) as any);
      }, POLLING_INTERVAL);
    } else {
      setIsVopPolling(false);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [VopPassed, File?.FileGuid, params.FileGuid, File?.ReturnValue?.length]);

  const handlePreview = async (type: string) => {
    setLoadingPreviewType(type);

    const { payload } = await dispatch(
      BulkFileActions.getBulkPaymentsPreview({
        fileGuid: params.FileGuid as string,
        type: type,
      }) as any
    );

    if (payload?.ErrorCaptions?.length) {
      setErrors(payload?.ErrorCaptions);
      setShowConfirm(false);
    } else {
      setShowConfirm(true);
    }
  };

  useEffect(() => {
    if (showConfirm) {
      refExcludedPayments.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [showConfirm]);

  const cancelApprove = (type: any, errorCode: any, notificationType: string | null = 'warning') => {
    setApprove(false);
    dispatch(BulkFileActions.cancelLoading());

    let message;

    switch (type) {
      case SIGN_CANCEL_TYPES.TIMEOUT:
        message = 'PaymentCanceledTimeout';
        break;
      case SIGN_CANCEL_TYPES.PMC10000:
        return null;
      case SIGN_CANCEL_TYPES.MANUAL:
        message = 'PaymentCanceledManual';
        break;
      case SIGN_CANCEL_TYPES.REJECTED:
        message = 'PaymentCanceledRejected';
        break;
      case SIGN_CANCEL_TYPES.ERROR_CODE:
        message = 'PaymentErrorCode';
        break;
      case SIGN_CANCEL_TYPES.VALIDATION:
        message = 'PaymentErrorValidation';
        break;
      case SIGN_CANCEL_TYPES.LIMITS_INSTANT:
        message = 'PaymentErrorLimitsInstant';
        break;
      default:
        message = errorCode?.replace('.', '');
        break;
    }

    Notification({
      type: notificationType,
      message: message ? i18.NotificationTitles[message as keyof typeof i18.NotificationTitles] : i18.NotificationTitles.Canceled,
      description: message ? i18.NotificationMessages[message as keyof typeof i18.NotificationMessages] : i18.NotificationMessages.Canceled,
    });
  };

  const clickApproveModal = async () => {
    const res = await dispatch(
      BulkFileActions.postBulkPaymentsSign({
        FileGuid: File?.FileGuid,
        Language: language,
        PaymentSystem: loadingPreviewType as string,
      }) as any
    );

    if (res.payload?.IsSuccessfull) {
      setApprove(true);
    } else {
      if (res.payload?.ErrorCode === SIGN_CANCEL_TYPES.PMC10000) {
        cancelApprove(SIGN_CANCEL_TYPES.PMC10000, null, null);
      } else if (Array.isArray(res.payload?.PaymentErrors) && res.payload?.PaymentErrors?.length) {
        if (res.payload?.PaymentErrors?.some((e: any) => e.ErrorCode === 'PMC0L010')) {
          cancelApprove(SIGN_CANCEL_TYPES.LIMITS_INSTANT, null, 'warning');
        } else {
          cancelApprove(SIGN_CANCEL_TYPES.VALIDATION, null, 'warning');
        }
      } else {
        cancelApprove(SIGN_CANCEL_TYPES.REJECTED, null, 'warning');
      }

      setShowConfirm(false);
      setLoadingPreviewType(null);
    }
  };

  const successApprove = () => {
    setApprove(false);
    setShowConfirm(false);
    dispatch(BulkFileActions.removeFromList(File?.FileGuid));
    navigate(`${routerPath.payments}/${routerPath.unsigned}/${routerPath.unsignedFiles}`, {
      state: {
        paymentSuccess: true,
      },
    });
    Notification({
      type: 'success',
      message: i18.NotificationTitles.SuccessApprove,
      description: i18.NotificationMessages.SuccessApprove,
    });
  };

  const submitSmsApprove = async (values: any) => {
    if (error) {
      return;
    }

    const response = await dispatch(OMCActions.postSUORSMS(values ?? { iSignToken: FilesListSign?.SMSToken }) as any);
    const IsSuccessfull = response?.payload?.IsSuccessfull;

    if (!IsSuccessfull) {
      cancelApprove(SIGN_CANCEL_TYPES.ERROR_CODE, null, 'error');
    } else {
      successApprove();
    }
  };

  const submitMobileApprove = async (values: any) => {
    if (error) {
      return;
    }

    const response = await dispatch(OMCActions.postSUOR(values ?? { iSignToken: FilesListSign?.iSignToken }) as any);
    const AuthType = response?.payload?.AuthType;
    const IsSuccessfull = response?.payload?.IsSuccessfull;
    const ErrorCode = response?.payload?.ErrorCode;

    if (AuthType === null && IsSuccessfull === false) {
      return true;
    }

    if (AuthType === 'false') {
      if (ErrorCode === 'loginError.canceled') {
        cancelApprove(SIGN_CANCEL_TYPES.REJECTED, ErrorCode);
      } else {
        cancelApprove(null, ErrorCode);
      }
    } else if (IsSuccessfull === true) {
      successApprove();
    }

    return false;
  };

  const refError = useRef(null);

  useEffect(() => {
    if (error || errors?.length > 0) {
      (refError?.current as any)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [error, errors]);

  return (
    <>
      {isVopPolling && (
        <div className="mb-4">
          <AlertNotification
            alertClassName="w-100"
            isLoading={isVopPolling}
            message={trb(i18.PaymentBulk.PaymentsInReviewTitle)}
            description={trb(i18.PaymentBulk.PaymentsInReview)}
            type="warning"
          />
        </div>
      )}
      <div ref={refError}>
        {error === SIGN_CANCEL_TYPES.PMC10000 && (
          <div className="mb-4">
            <AlertNotification
              alertClassName="w-100"
              message=""
              description={trb(translationGroups.ErrorCode + error)}
              type="info"
            />
          </div>
        )}
        {errors?.length > 0 && <PaymentError errors={errors} />}
      </div>
      {isOutgoingPaymentsBlocked && <BlockedOutgoingPaymentsNotification />}
      <SummaryUnsignedPaymentFiles data={File} />
      {!showConfirm ? (
        <div>
          {File?.ReturnValue?.length > 0 ? (
            <>
              <TableUnsignedPaymentFiles
                title={
                  <>
                    {trb(i18.Labels.Payments)} ({File?.ReturnValue?.length})
                  </>
                }
                data={File?.ReturnValue}
              />
              <div className="mb-3 text-right">
                <Space>
                  <Button
                    id="UnsignedPaymentFileDetailsConfirmButton"
                    size="large"
                    type="primary"
                    onClick={() => handlePreview('SCT')}
                    disabled={!VopPassed || loadingPreviewType === 'INST' || isOutgoingPaymentsBlocked}
                    loading={loadingPreviewType === 'SCT'}
                  >
                    {trb(i18.Buttons.PaySCT)}
                  </Button>
                  <Button
                    id="UnsignedPaymentFileDetailsConfirmButton"
                    size="large"
                    type="primary"
                    onClick={() => handlePreview('INST')}
                    disabled={!VopPassed || loadingPreviewType === 'SCT' || isOutgoingPaymentsBlocked}
                    loading={loadingPreviewType === 'INST'}
                  >
                    {trb(i18.Buttons.PayINST)}
                  </Button>
                </Space>
              </div>
            </>
          ) : (
            <ContentNoData id="UnsignedPaymentFiles" />
          )}
        </div>
      ) : (
        <div>
          <TableUnsignedPaymentFiles
            title={
              <>
                {trb(i18.Labels.PaymentsIncluded)} ({FilesListPreview?.IncludedPayments?.length})
              </>
            }
            data={FilesListPreview?.IncludedPayments}
          />
          <div ref={refExcludedPayments}>
            {FilesListPreview?.ExcludedPayments?.length > 0 && (
              <TableUnsignedPaymentFiles
                title={
                  <>
                    {trb(i18.Labels.PaymentsExcluded)} ({FilesListPreview?.ExcludedPayments?.length})
                  </>
                }
                data={FilesListPreview?.ExcludedPayments}
              />
            )}
          </div>
          {FilesListPreview?.Report?.length > 0 && <ReportUnsignedPaymentFiles data={FilesListPreview?.Report} />}
          <div className="mb-3 text-right">
            <Space>
              <Button
                id="UnsignedPaymentFileDetailsCancelButton"
                type="default"
                size="large"
                disabled={loading || approve}
                onClick={() => {
                  if (sendSUOR || sendSUORSMS) {
                    return;
                  }

                  setShowConfirm(false);
                  setApprove(false);
                  dispatch(BulkFileActions.cancelLoading());
                  setLoadingPreviewType(null);
                }}
              >
                {trb(i18.Buttons.Cancel)}
              </Button>
              {FilesListPreview?.IncludedPayments?.length > 0 && (
                <Button
                  id="UnsignedPaymentFileDetailsSubmitButton"
                  type="primary"
                  size="large"
                  onClick={clickApproveModal}
                  loading={loading || approve}
                >
                  {trb(i18.Buttons.Approve)}
                </Button>
              )}
            </Space>
          </div>
        </div>
      )}
      {approve && FilesListSign && (
        <div className="pb-3">
          <OperationApproval
            RQ_ID={FilesListSign?.iSignToken ?? FilesListSign?.SMSToken}
            AuthType={AuthType}
            code={FilesListSign?.ControlCode}
            cancelApprove={cancelApprove}
            successApprove={successApprove}
            submitSmsApprove={submitSmsApprove}
            submitMobileApprove={submitMobileApprove}
            approveCount={FilesListPreview?.IncludedPayments?.length}
            errorCode={error}
            loading={sendSUOR || sendSUORSMS}
          />
        </div>
      )}
    </>
  );
};
